if (!customElements.get('header-claude')) {
  class HeaderClaude extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.hd');
      this.drawer = this.querySelector('.hd__drawer');
      this.openBtn = this.querySelector('[data-hd-open]');
      this.sticky = this.dataset.sticky;
      this.collapse = this.dataset.collapse === 'true';
      this.collapseAt = parseInt(this.dataset.collapseOffset, 10) || 60;
      this.searchToggle = this.querySelector('[data-hd-search-toggle]');
      this.mSearch = this.querySelector('.hd__search--m');
      this.transparent = this.dataset.transparent === 'true';
      this.lastY = window.scrollY;
      this.compact = false;
      this.lockUntil = 0;

      // Hauteur exposée pour la superposition sur le hero
      this.ro = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--hdc-height', this.bar.offsetHeight + 'px');
      });
      this.ro.observe(this.bar);

      // Défilement
      this.onScroll = () => {
        const y = window.scrollY;
        if (this.transparent && this.sticky !== 'none') this.bar.classList.toggle('is-solid', y > 10);
        if (this.collapse) {
          // Hystérésis + verrou pendant l'animation : replier/déplier la barre change
          // la hauteur de la page, ce qui décale le défilement et provoquait un clignotement.
          const now = Date.now();
          if (now < this.lockUntil) {
            clearTimeout(this.recheck);
            this.recheck = setTimeout(this.onScroll, this.lockUntil - now + 20);
          } else {
            let compact = this.compact;
            if (!compact && y > this.collapseAt) compact = true;
            else if (compact && y <= 4) compact = false;
            if (compact !== this.compact) {
              this.compact = compact;
              this.lockUntil = now + 450;
              this.bar.classList.toggle('is-compact', compact);
              if (!compact) this.closeSearch();
            }
          }
        }
        if (this.sticky === 'scroll_up') {
          const h = this.bar.offsetHeight;
          if (y > this.lastY && y > h * 2 && !this.isOpen) this.bar.classList.add('is-hidden');
          else if (y < this.lastY - 4 || y <= h) this.bar.classList.remove('is-hidden');
        }
        this.lastY = y;
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      // Recherche mobile repliable
      this.searchToggle?.addEventListener('click', () => {
        const open = this.bar.classList.toggle('search-open');
        this.searchToggle.setAttribute('aria-expanded', String(open));
        this.searchToggle.setAttribute('aria-label', open ? 'Masquer la recherche' : 'Afficher la recherche');
        if (open) setTimeout(() => this.mSearch?.querySelector('input')?.focus(), 250);
      });
      this.mSearch?.addEventListener('focusout', (e) => {
        if (!this.mSearch.contains(e.relatedTarget) && !this.mSearch.querySelector('input')?.value) this.closeSearch();
      });

      // Tiroir : navigation par niveaux
      this.levels = Array.from(this.querySelectorAll('[data-hd-level]'));
      this.path = ['root'];
      this.querySelectorAll('[data-hd-next]').forEach((btn) => btn.addEventListener('click', () => this.goLevel(btn.dataset.hdNext)));
      this.querySelectorAll('[data-hd-back]').forEach((btn) => btn.addEventListener('click', () => this.backLevel()));

      // Tiroir
      this.openBtn?.addEventListener('click', () => this.open());
      this.querySelectorAll('[data-hd-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.onKey = (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); };
      document.addEventListener('keydown', this.onKey);
      this.mq = window.matchMedia('(min-width: 990px)');
      this.onMq = () => { if (this.mq.matches && this.isOpen) this.close(); };
      this.mq.addEventListener('change', this.onMq);

      // Compteur panier
      this.onCart = () => this.refreshCart();
      ['cart:updated', 'cart:refresh', 'cart:change', 'product:added', 'product-added', 'ajaxProduct:added'].forEach((ev) => document.addEventListener(ev, this.onCart));
    }

    setLevel(id, back) {
      this.levels.forEach((el) => {
        const on = el.dataset.hdLevel === id;
        el.classList.toggle('is-active', on);
        el.classList.toggle('is-past', !on && !back && this.path.includes(el.dataset.hdLevel));
        el.setAttribute('aria-hidden', String(!on));
        if ('inert' in el) el.inert = !on;
        if (on) { el.scrollTop = 0; setTimeout(() => el.querySelector('.hd__back, .hd__m-link')?.focus({ preventScroll: true }), 300); }
      });
    }

    goLevel(id) {
      if (!id) return;
      this.path.push(id);
      this.setLevel(id, false);
    }

    backLevel() {
      if (this.path.length < 2) return;
      this.path.pop();
      this.setLevel(this.path[this.path.length - 1], true);
    }

    closeSearch() {
      if (!this.bar.classList.contains('search-open')) return;
      this.bar.classList.remove('search-open');
      this.searchToggle?.setAttribute('aria-expanded', 'false');
      this.searchToggle?.setAttribute('aria-label', 'Afficher la recherche');
    }

    disconnectedCallback() {
      this.ro?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('keydown', this.onKey);
      this.mq?.removeEventListener('change', this.onMq);
      document.body.style.overflow = '';
    }

    open() {
      this.isOpen = true;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.openBtn?.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      setTimeout(() => this.drawer.querySelector('.hd__panel [data-hd-close]')?.focus(), 50);
    }

    close() {
      this.isOpen = false;
      this.path = ['root'];
      this.setLevel('root', true);
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.openBtn?.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      this.openBtn?.focus();
    }

    async refreshCart() {
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        this.querySelectorAll('[data-hd-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
        this.querySelector('[data-hd-cart]')?.setAttribute('aria-label', 'Panier (' + cart.item_count + ')');
      } catch (e) {}
    }
  }
  customElements.define('header-claude', HeaderClaude);
}